import {TFunction} from 'i18next';
import isMobile from 'is-mobile';
import {filter, find, findKey, reduce, some, transform} from 'lodash';

import {SCREEN_RECORDER_MEDIA_LABEL} from 'constants/session';
import {
    DEVICES_NOT_FOUND_ERROR, NOT_ALLOWED_ERROR, NOT_FOUND_ERROR, OVERCONSTRAINED_ERROR,
    PERMISSION_DENIED_ERROR, SECURITY_ERROR
} from 'constants/webrtc';
import {DeviceKind, FacingMode} from 'enums';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {MediaDevicesStorageData} from 'services';
import {MediaDevicesConstraints} from 'types';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.MEDIA_DEVICES_MODAL;
const ROOM_ERROR_T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.WEBRTC.ROOM_ERROR_DIALOG;

const DEVICE_NOT_FOUND_ERRORS = [NOT_FOUND_ERROR, DEVICES_NOT_FOUND_ERROR, OVERCONSTRAINED_ERROR];
const PERMISSION_ERRORS = [NOT_ALLOWED_ERROR, PERMISSION_DENIED_ERROR, SECURITY_ERROR];

// getUserMedia rejects for reasons that need different reactions: a missing camera can not be fixed by granting
// permissions, so the real cause is reported instead of always blaming permissions. Unknown reasons fall back to
// the generic dialog, and the code is passed through so the agent side stops seeing every failure as "denied"
const getUserMediaErrorDetails = (error: {name?: string}): {code: string, message: string, dialogKey: string} => {
    if (DEVICE_NOT_FOUND_ERRORS.includes(error?.name)) {
        return {code: error.name, message: 'Media device not found', dialogKey: ROOM_ERROR_T_KEYS.DEVICE_NOT_FOUND};
    }
    if (PERMISSION_ERRORS.includes(error?.name)) {
        return {code: NOT_ALLOWED_ERROR, message: 'Cannot access user media', dialogKey: ROOM_ERROR_T_KEYS.NOT_ALLOWED};
    }
    return {
        code: error?.name || NOT_ALLOWED_ERROR,
        message: 'Cannot access user media',
        dialogKey: ROOM_ERROR_T_KEYS.UNEXPECTED_ERROR
    };
};

const getMediaDevices = (devices: MediaDeviceInfo[], t: TFunction): MediaDeviceInfo[] => devices
    .filter((mediaDevice) => mediaDevice.label !== SCREEN_RECORDER_MEDIA_LABEL)
    .map((device, index) => {
        const deviceNameTKey = findKey(DeviceKind, (key) => key === device.kind).toUpperCase();
        return ({
        // properties one by one as device is not iterable
            deviceId: device.deviceId,
            groupId: device.groupId,
            kind: device.kind,
            label: device.label || `${t(T_KEYS[deviceNameTKey] || T_KEYS.DEFAULT_DEVICE)} ${++index}`,
        } as MediaDeviceInfo);
    });

const filterDevicesByKind = (devices: MediaDeviceInfo[], kind: MediaDeviceKind): MediaDeviceInfo[] =>
    filter(devices, (device) => device.kind === kind);

const filterCameras = (devices: MediaDeviceInfo[]): MediaDeviceInfo[] => filterDevicesByKind(devices, 'videoinput');
const filterMicrophones = (devices: MediaDeviceInfo[]): MediaDeviceInfo[] => filterDevicesByKind(devices, 'audioinput');

const getDevicesOptions = (kind: MediaDeviceKind, devices: MediaDeviceInfo[]): Option[] =>
    filterDevicesByKind(devices, kind).map((device) => ({id: device?.deviceId, name: device?.label}));

const findDefaultDevice = (kind: MediaDeviceKind, devices: MediaDeviceInfo[]): MediaDeviceInfo => {
    const kindDevices = filterDevicesByKind(devices, kind);
    return find(kindDevices, (device) => device.deviceId === 'default') || kindDevices?.[0];
};

const getSelectedDevicesOrDefault = (
    availableDevices: MediaDeviceInfo[], selectedDevices: MediaDevicesStorageData
): MediaDevicesStorageData =>
    transform(DeviceKind, (result, deviceKind) => {
        result[deviceKind] = some(availableDevices, {deviceId: selectedDevices?.[deviceKind]}) ?
            selectedDevices?.[deviceKind] : findDefaultDevice(deviceKind, availableDevices)?.deviceId;
    }, {});

const getMediaTrackConstraintSet = (deviceId = 'default'): MediaTrackConstraintSet => ({deviceId: {ideal: deviceId}});

const getVideoTrackConstraintSet = (deviceId = 'default', facingMode = FacingMode.User): MediaTrackConstraintSet => {
    if (isMobile({tablet: true})) {
        return {facingMode: {exact: facingMode}};
    }
    return getMediaTrackConstraintSet(deviceId);
};

const cleanMediaStream = (stream: MediaStream) => {
    stream?.getTracks().forEach((track) => {
        track.stop();
    });
};

const canChangeMediaSettings = () => !isMobile({tablet: true});

const isUsedInputDevicesToggled = (
    prevDevicesList: MediaDevicesConstraints, currentDevicesList: MediaDevicesConstraints
): boolean => reduce([DeviceKind.Microphone, DeviceKind.Camera],
    (result: boolean, kind) => result || !!prevDevicesList?.[kind] && !currentDevicesList?.[kind] ||
            !prevDevicesList?.[kind] && !!currentDevicesList?.[kind], false);

const hasSupportRestartDevices = () =>
    // We need this check as Safari on IOS doesn't support replaceTrack() method.
    // Android have permissions issue with calling getUserMedia() method after switching camera for the second time.
    // Without this check we would have a problem with switching between front/back cameras (video will disappear after changing)
    !isMobile({tablet: true})
;

export const mediaDevicesUtils = {
    getMediaDevices,
    getUserMediaErrorDetails,
    filterDevicesByKind,
    filterCameras,
    filterMicrophones,
    findDefaultDevice,
    getDevicesOptions,
    getSelectedDevicesOrDefault,
    cleanMediaStream,
    getMediaTrackConstraintSet,
    getVideoTrackConstraintSet,
    canChangeMediaSettings,
    isUsedInputDevicesToggled,
    hasSupportRestartDevices
};
